<template>
  <nav class="sidebar">
    <ul class="nav">
      <li class="nav-item logo">
        <RouterLink to="/">
          <Logo />
        </RouterLink>
      </li>
      <li class="nav-item" :class="{ 'has-active': isActive('/') }">
        <RouterLink to="/" activeClass="active">
          <div class="nav-n">001.</div>
          <div class="nav-title">work</div>
        </RouterLink>
      </li>
      <li class="nav-item" :class="{ 'has-active': isActive('/profile') }">
        <RouterLink to="/profile" activeClass="active">
          <div class="nav-n">002.</div>
          <div class="nav-title">profile</div>
        </RouterLink>
      </li>
      <li class="nav-item" :class="{ 'has-active': isActive('/writing') }">
        <RouterLink to="/writing" activeClass="active">
          <div class="nav-n">003.</div>
          <div class="nav-title">words</div>
        </RouterLink>
      </li>
      <li class="nav-item" :class="{ 'has-active': isActive('/contact') }">
        <RouterLink to="/contact" activeClass="active">
          <div class="nav-n">004.</div>
          <div class="nav-title">contact</div>
        </RouterLink>
      </li>
    </ul>

    <div class="nav-wrapper">
      <div class="row">
        <div class="column">
          <div
            class="intro intro-index"
            ref="titleElement"
            :class="{ 'animate-out': isAnimating }"
          >
            <span>{{ currentTitle }}</span>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="column column-offset-50 mobile-full">
          <div class="description-container">
            <p ref="descriptionElement">
              {{ currentDescription }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <SiteFooter class="lg-only" />
  </nav>
</template>

<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  defineProps,
  defineExpose,
  watch,
} from "vue";
import SiteFooter from "@/components/layout/SiteFooter.vue";
import { useRoute } from "vue-router";
import Logo from "@/components/Logo.vue";

const route = useRoute();

const isActive = (path) => route.path === path;

const props = defineProps({
  title: String,
  description: String,
});

// Animation configuration
const animationDuration = 400; // milliseconds
const titleAnimationDuration = 200; // milliseconds (slower for title)
const descriptionAnimationDuration = 300; // milliseconds

// Reactive state
const mouseX = ref(0);
const mouseY = ref(0);
const isAnimating = ref(false);
const currentTitle = ref(props.title);
const currentDescription = ref(props.description);

// Template refs
const titleElement = ref(null);
const descriptionElement = ref(null);

// Event handlers
const handleMouseMove = (event) => {
  mouseX.value = event.clientX;
  mouseY.value = event.clientY;
};

// Animation helper
const setElementStyles = (element, styles) => {
  if (!element) return;
  Object.assign(element.style, styles);
};

const animateContentChange = (newTitle, newDescription) => {
  const titleTextFadeOutStyles = {
    transition: `opacity ${titleAnimationDuration}ms ease`,
    opacity: "0",
  };

  const titleTextFadeInStyles = {
    transition: `opacity 1000ms ease`,
    opacity: "1",
  };

  const descriptionFadeOutStyles = {
    transition: `opacity ${descriptionAnimationDuration}ms ease, transform ${descriptionAnimationDuration}ms ease`,
    opacity: "0",
    transform: "translateY(-20px)",
  };

  const descriptionFadeInStyles = {
    transition: `opacity ${descriptionAnimationDuration}ms ease, transform ${descriptionAnimationDuration}ms ease`,
    opacity: "1",
    transform: "translateY(0)",
  };

  // Get the span element inside the title
  const titleSpan = titleElement.value?.querySelector("span");

  // Step 1: Start background scaling down and fade out current content
  isAnimating.value = true;
  if (titleSpan) {
    setElementStyles(titleSpan, titleTextFadeOutStyles);
  }
  setElementStyles(descriptionElement.value, descriptionFadeOutStyles);

  // Step 2: Update content after fade out
  setTimeout(() => {
    currentTitle.value = newTitle;
    currentDescription.value = newDescription;

    // Step 3: Scale background back up and fade in new content
    setTimeout(() => {
      isAnimating.value = false;

      // Start content animations after background starts scaling back up
      setTimeout(() => {
        if (titleSpan) {
          setElementStyles(titleSpan, titleTextFadeInStyles);
        }
        setElementStyles(descriptionElement.value, descriptionFadeInStyles);
      }, 100); // Slightly longer delay for title text
    }, 50);
  }, animationDuration);
};

// Watch for prop changes
watch(
  () => [props.title, props.description],
  ([newTitle, newDescription]) => {
    if (
      newTitle !== currentTitle.value ||
      newDescription !== currentDescription.value
    ) {
      animateContentChange(newTitle, newDescription);
    }
  }
);

// Animation controls
const triggerAnimation = () => {
  isAnimating.value = true;
};

const expandAnimation = () => {
  isAnimating.value = false;
};

// Lifecycle
onMounted(() => {
  window.addEventListener("mousemove", handleMouseMove);
  currentTitle.value = props.title;
  currentDescription.value = props.description;
});

onUnmounted(() => {
  window.removeEventListener("mousemove", handleMouseMove);
});

defineExpose({
  triggerAnimation,
  expandAnimation,
});
</script>

<style scoped lang="scss">
.intro {
  line-height: 1.25em;
  text-align: left;
  text-indent: 64px;
  text-transform: uppercase;
  margin-bottom: 2rem;
  font-family: "Geist";
  font-weight: 200;
  color: white;
  padding: 2rem;

  span {
    margin: 0;
    padding: 0;
    position: relative;
    z-index: 2;
  }

  &.intro-index {
    position: relative;
    margin-bottom: 1rem;
    /* font-size: clamp(2rem, 10vw, 8rem); */
    font-size: clamp(5rem, 5vw, 8.5rem);

    &::before {
      content: "";
      position: absolute;
      left: -1rem;
      top: 50%;
      transform: translateY(-50%);
      width: calc(100% + 2rem);
      height: 100%;
      background: black;
      z-index: 1;
      transition: height 400ms cubic-bezier(0.77, 0, 0.18, 1);
    }

    &.animate-out::before {
      height: 0%;
    }
  }
}

.nav-wrapper {
  position: relative;
  padding: 0.5rem 0;

  @media (max-width: 40rem) {
    margin-bottom: 1rem;
  }
}

.description-container {
  display: flex;
  align-items: flex-start;

  p {
    margin: 0;
  }
}
</style>
